import React from 'react';
import {useForm} from 'react-hook-form';
import Swal from 'sweetalert2';

function GatewayClinicInfoForm({clinicInfo, updateInfo}) {
    const {register, handleSubmit, formState: {errors}} = useForm({
        defaultValues: clinicInfo
    });

    const onSubmit = (data) => {
        const updatePayload = {
            id: clinicInfo.id,
            name: data.name,
            email: data.email,
            phone: data.phone ? data.phone.replace(/\D/g, '') : '',
            signature: data.signature,
            website: data.website,
            street: data.street,
            city: data.city,
            postalCode: data.postalCode,
            province: data.province,
            country: data.country,
            clinicType: clinicInfo.clinicType,
            sms: clinicInfo.sms,
            logo: clinicInfo.logo,
        };

        Swal.fire({
            title: 'Save clinic information?',
            html: `<div class="text-center">
                     <i class="bi bi-building text-primary fs-1 mb-3 d-block"></i>
                     <p class="small mb-0 text-muted">Apply your changes to <strong>${data.name}</strong>?</p>
                   </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: '<i class="bi bi-check-lg"></i> Yes, save',
            cancelButtonText: '<i class="bi bi-x"></i> Cancel',
            customClass: {
                title: 'card-title',
                confirmButton: 'btn btn-sm me-2 gateway-clinic-swal-confirm',
                cancelButton: 'btn btn-sm gateway-clinic-swal-cancel',
            },
            buttonsStyling: false,
            showClass: {
                popup: 'animate__animated animate__fadeInDown animate__faster',
            },
            hideClass: {
                popup: 'animate__animated animate__fadeOutUp animate__faster',
            },
            showLoaderOnConfirm: true,
            allowOutsideClick: () => !Swal.isLoading(),
            preConfirm: () => {
                return updateInfo(updatePayload).catch((err) => {
                    const message = err?.response?.data?.message
                        || err?.response?.data?.error
                        || 'Failed to update clinic information';
                    Swal.showValidationMessage(message);
                    return Promise.reject(err);
                });
            },
        }).then((result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'Saved!',
                    text: 'Clinic information updated successfully.',
                    icon: 'success',
                    confirmButtonText: '<i class="bi bi-check-lg"></i> OK',
                    customClass: {
                        title: 'card-title',
                        confirmButton: 'btn btn-sm gateway-clinic-swal-confirm',
                    },
                    buttonsStyling: false,
                    showClass: {
                        popup: 'animate__animated animate__fadeInDown animate__faster',
                    },
                    hideClass: {
                        popup: 'animate__animated animate__fadeOutUp animate__faster',
                    },
                    timer: 3000,
                    timerProgressBar: true,
                });
            }
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="clinic-info-content">
            <section className="clinic-info-form-section">
                <h6 className="clinic-info-section-title fw-normal text-primary">
                    <i className="bi bi-info-circle me-2"></i> Basic Information
                </h6>
                <div className="row clinic-info-form-fields">
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="name" className="form-label">
                                <i className="bi bi-building me-1"></i> Clinic Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                {...register("name", {required: "Clinic name is required"})}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>

                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="number" className="form-label">
                                <i className="bi bi-hash me-1"></i> Clinic ID
                            </label>
                            <input
                                type="text"
                                id="number"
                                {...register("number")}
                                disabled
                                className="form-control bg-light"
                            />
                        </div>

                        {/* Email */}
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="email" className="form-label">
                                <i className="bi bi-envelope me-1"></i> Email
                            </label>
                            <input
                                type="email"
                                id="email"
                                {...register("email", {
                                    required: "Email is required",
                                    pattern: {
                                        value: /^\S+@\S+$/i,
                                        message: "Invalid email format"
                                    }
                                })}
                                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            />
                            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
                        </div>

                        {/* Phone */}
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="phone" className="form-label">
                                <i className="bi bi-telephone me-1"></i> Phone
                            </label>
                            <input
                                type="tel"
                                id="phone"
                                {...register("phone", {required: "Phone number is required"})}
                                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                            />
                            {errors.phone && <div className="invalid-feedback">{errors.phone.message}</div>}
                        </div>

                        {/* SMS (Disabled) */}
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="sms" className="form-label">
                                <i className="bi bi-chat-left-text me-1"></i> SMS Number
                            </label>
                            <input
                                type="text"
                                id="sms"
                                {...register("sms")}
                                disabled
                                className="form-control bg-light"
                            />
                        </div>

                        {/* Signature */}
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="signature" className="form-label">
                                <i className="bi bi-pen me-1"></i> Signature
                            </label>
                            <input
                                type="text"
                                id="signature"
                                {...register("signature")}
                                className="form-control"
                            />
                        </div>

                        {/* Website */}
                        <div className="col-md-6 clinic-info-form-group">
                            <label htmlFor="website" className="form-label">
                                <i className="bi bi-globe me-1"></i> Website
                            </label>
                            <input
                                type="url"
                                id="website"
                                {...register("website")}
                                className="form-control"
                                placeholder="https://"
                            />
                        </div>

                </div>
            </section>

            <hr className="clinic-info-divider"/>

            <section className="clinic-info-form-section">
                <h6 className="clinic-info-section-title fw-normal text-primary">
                    <i className="bi bi-geo-alt me-2"></i> Address Information
                </h6>
                <div className="row clinic-info-form-fields">
                        <div className="col-md-8 clinic-info-form-group">
                            <label htmlFor="street" className="form-label">
                                Street Address
                            </label>
                            <input
                                type="text"
                                id="street"
                                {...register("street", {required: "Street address is required"})}
                                className={`form-control ${errors.street ? 'is-invalid' : ''}`}
                            />
                            {errors.street && <div className="invalid-feedback">{errors.street.message}</div>}
                        </div>

                        {/* City */}
                        <div className="col-md-4 clinic-info-form-group">
                            <label htmlFor="city" className="form-label">
                                City
                            </label>
                            <input
                                type="text"
                                id="city"
                                {...register("city", {required: "City is required"})}
                                className={`form-control ${errors.city ? 'is-invalid' : ''}`}
                            />
                            {errors.city && <div className="invalid-feedback">{errors.city.message}</div>}
                        </div>

                        {/* Postal Code */}
                        <div className="col-md-4 clinic-info-form-group">
                            <label htmlFor="postalCode" className="form-label">
                                Postal Code
                            </label>
                            <input
                                type="text"
                                id="postalCode"
                                {...register("postalCode", {required: "Postal code is required"})}
                                className={`form-control ${errors.postalCode ? 'is-invalid' : ''}`}
                            />
                            {errors.postalCode && <div className="invalid-feedback">{errors.postalCode.message}</div>}
                        </div>

                        {/* Province */}
                        <div className="col-md-4 clinic-info-form-group">
                            <label htmlFor="province" className="form-label">
                                Province/State
                            </label>
                            <input
                                type="text"
                                id="province"
                                {...register("province", {required: "Province is required"})}
                                className={`form-control ${errors.province ? 'is-invalid' : ''}`}
                            />
                            {errors.province && <div className="invalid-feedback">{errors.province.message}</div>}
                        </div>

                        {/* Country */}
                        <div className="col-md-4 clinic-info-form-group">
                            <label htmlFor="country" className="form-label">
                                Country
                            </label>
                            <input
                                type="text"
                                id="country"
                                {...register("country", {required: "Country is required"})}
                                className={`form-control ${errors.country ? 'is-invalid' : ''}`}
                            />
                            {errors.country && <div className="invalid-feedback">{errors.country.message}</div>}
                        </div>
                </div>
            </section>

            <div className="d-flex justify-content-end clinic-info-form-actions">
                <button type="submit" className="btn btn-success px-4 text-white">
                    <i className="bi bi-save me-2"></i> Save Changes
                </button>
            </div>
        </form>
    );
}

export default GatewayClinicInfoForm;